import { em } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'
import ReactDOM from 'react-dom'
import { Transition } from 'react-transition-group'

import ButtonClose from 'components/ButtonClose'

import {
  ButtonCloseWrapper,
  TooltipArrow,
  TooltipContainer,
  TooltipContent,
  TooltipInner,
} from './styles'

const TOP = 'top'
const BOTTOM = 'bottom'
const LEFT = 'left'
const RIGHT = 'right'
const VERT_KEYS = [TOP, BOTTOM]
const HORIZ_KEYS = [LEFT, RIGHT]
const SELECTOR = '#tooltip-root'
const ANIMATION_ENTER = 300
const ANIMATION_EXIT = 300
const ARROW_SIZE = 10
const ARROW_TO_BUTTON_INDENT = 5
const TOOLTIP_INDENTS = {
  left: 20,
  top: 20,
  right: 20,
  bottom: 20,
}
const DELTA_ARROW = {
  x: 0,
  y: 0,
}

/**
 * NEED TO DO POSSIBILITY TO SHOW TOOLTIP ON THE LEFT SIDE OF BUTTON
 *
 * Tooltip component that render tooltip and it's content in the portal
 *
 * @returns { Tooltip } Tooltip — Component of Tooltip
 */

const Tooltip = ({
  children,
  isOpen,
  originRef,
  position,
  deltaArrow,
  topIndentNode,
  leftIndentNode,
  withCloseIcon,
  onCrossClick,
  indents,
}) => {
  const safeDeltaArrow = React.useMemo(
    () => ({ ...DELTA_ARROW, ...deltaArrow }),
    [deltaArrow],
  )
  const mergedIndents = React.useMemo(
    () => ({ ...TOOLTIP_INDENTS, ...indents }),
    [indents],
  )
  const domNode = React.useRef(document.querySelector(SELECTOR))
  const tooltipRef = React.useRef(null)
  const arrowRef = React.useRef(null)

  const [arrowStyles, setArrowStyles] = React.useState(() => ({
    left: 0,
    top: 0,
  }))
  const [ttStyles, setTTstyles] = React.useState(() => ({
    left: 0,
    top: 0,
    bottom: 'auto',
  }))
  const [ttHeight, setTTheight] = React.useState(0)
  const prevY = React.useRef(window.pageYOffset)

  const arrowPositionPostfix = React.useMemo(() => {
    switch (position) {
      case TOP:
        return 'down'
      case BOTTOM:
        return 'up'
      case LEFT:
        return 'right'
      case RIGHT:
        return 'left'
      default:
        throw new Error('position must be set up correctly')
    }
  }, [position])

  /**
   * @param {object} button - object with BoundingClientRect for button
   * @param {object} tooltip - object with BoundingClientRect for tooltip
   *
   * @returns {object} — calculated horizontal parameters for tooltip
   */
  const calculateHorizontally = React.useCallback(
    (button, tooltip) => {
      const result = {}
      const windowWidth = window.innerWidth
      const { left, right, width } = button
      const buttonCenterX = Math.round(left + width / 2)
      const spaceForArrow = ARROW_SIZE + ARROW_TO_BUTTON_INDENT

      let leftIndent = 0
      if (leftIndentNode?.current) {
        leftIndent = leftIndentNode.current.getBoundingClientRect().left
      }
      const tooltipWidth =
        position === LEFT ? left - spaceForArrow - leftIndent : tooltip.width
      let tooltipLeftLimit = leftIndent
      let tooltipRightLimit = windowWidth - tooltipWidth

      if (HORIZ_KEYS.includes(position)) {
        tooltipLeftLimit = position === RIGHT ? right + spaceForArrow : 0
        tooltipRightLimit =
          position === LEFT ? left - spaceForArrow : tooltipRightLimit
      }

      result.left =
        position === LEFT
          ? left - tooltipWidth + safeDeltaArrow.x - spaceForArrow
          : buttonCenterX - tooltipWidth / 2 + safeDeltaArrow.x

      if (result.left < tooltipLeftLimit) {
        result.left = tooltipLeftLimit
      } else if (result.left > tooltipRightLimit) {
        result.left = tooltipRightLimit
      }

      if (HORIZ_KEYS.includes(position)) {
        result.maxWidth =
          position === LEFT
            ? `${tooltipWidth}px`
            : `${windowWidth - result.left}px`
      }

      const diff = windowWidth - tooltipWidth - result.left
      if (diff < 0) {
        result.maxWidth = `${windowWidth - result.left}px`
      }

      return result
    },
    [leftIndentNode, position, safeDeltaArrow.x],
  )

  /**
   * @param {object} button - object with BoundingClientRect for button
   * @param {object} tooltip - object with BoundingClientRect for tooltip
   *
   * @returns {object} — calculated vertical parameters for tooltip
   */
  const calculateVertically = React.useCallback(
    (button, tooltip) => {
      const result = {}
      const { top, bottom, height } = button
      const [vertActiveKey] = VERT_KEYS.filter(vertKey => vertKey !== position)

      result.bottom = 'auto'
      prevY.current = window.pageYOffset

      if (VERT_KEYS.includes(position)) {
        result.top =
          prevY.current +
          (vertActiveKey === TOP
            ? bottom + ARROW_SIZE + ARROW_TO_BUTTON_INDENT + safeDeltaArrow.y
            : top -
              (tooltip.height +
                ARROW_SIZE +
                ARROW_TO_BUTTON_INDENT +
                safeDeltaArrow.y))
      } else {
        const topIndent = topIndentNode?.getBoundingClientRect().height

        const buttonCenterY = top + height
        const spaceForArrow = ARROW_SIZE * 2 + ARROW_TO_BUTTON_INDENT
        const tooltipTopLimit = buttonCenterY - tooltip.height + spaceForArrow
        const tooltipBottomLimit = top - spaceForArrow
        result.top =
          Math.min(Math.max(topIndent, tooltipTopLimit), tooltipBottomLimit) +
          prevY.current
      }

      return result
    },
    [safeDeltaArrow, position, topIndentNode],
  )

  /**
   * @returns {object} — calculated paddings for tooltip's wrapper
   */
  const calculatedPaddings = React.useMemo(() => {
    return {
      paddingLeft: position === RIGHT ? 0 : `${em(mergedIndents.left)}`,
      paddingRight: position === LEFT ? 0 : `${em(mergedIndents.right)}`,
      paddingTop: position === BOTTOM ? 0 : `${em(mergedIndents.top)}`,
      paddingBottom: position === TOP ? 0 : `${em(mergedIndents.bottom)}`,
    }
  }, [
    mergedIndents.bottom,
    mergedIndents.left,
    mergedIndents.right,
    mergedIndents.top,
    position,
  ])

  /**
   * @param {object} button - object with BoundingClientRect for button
   * @param {object} ttHorizStyles - object with calculated horizontal styles for tooltip
   *
   * @returns {object} — calculated horizontal parameters for tooltip
   */
  const calculateArrow = React.useCallback(
    (button, ttHorizStyles) => {
      const result = {
        left: position === LEFT ? 'auto' : 0,
        right: position === LEFT ? 0 : 'auto',
        top: position === TOP ? 'auto' : 0,
        bottom: position === TOP ? 0 : 'auto',
      }
      const { left, top, height, width } = button
      const buttonCenterX = Math.round(left + width / 2)
      const buttonCenterY = Math.round(top + height / 2)

      if (VERT_KEYS.includes(position)) {
        result.left =
          buttonCenterX -
          ttHorizStyles.left -
          mergedIndents.left +
          safeDeltaArrow.x
      } else {
        if (position !== LEFT) result.left = 0 + safeDeltaArrow.x
        result.top =
          buttonCenterY -
          ttHorizStyles.top -
          mergedIndents.top +
          safeDeltaArrow.y +
          prevY.current
      }

      return result
    },
    [position, safeDeltaArrow, mergedIndents.left, mergedIndents.top],
  )

  React.useEffect(() => {
    let nextTTStyles = {}
    if (isOpen) {
      const button = originRef.current.getBoundingClientRect()

      const buttonShifted = {
        left: button.left,
        top: button.top,
        bottom: button.bottom,
        right: button.right,
        x: button.left,
        y: button.top,
        width: button.width,
        height: button.height,
      }
      const tooltip = tooltipRef.current.getBoundingClientRect()

      const ttHorizStyles = calculateHorizontally(buttonShifted, tooltip)

      nextTTStyles = {
        ...calculateVertically(buttonShifted, tooltip),
        ...ttHorizStyles,
        ...calculatedPaddings,
      }
      setTTstyles(nextTTStyles)

      const arrowStyles = calculateArrow(buttonShifted, nextTTStyles)
      setArrowStyles(arrowStyles)

      if (tooltip.height !== ttHeight) {
        setTTheight(tooltip.height)
      }
    }
  }, [
    isOpen,
    ttHeight,
    position,
    originRef,
    calculateHorizontally,
    calculateVertically,
    calculatedPaddings,
    calculateArrow,
  ])

  const renderTooltip = React.useCallback(() => {
    return (
      <Transition
        mountOnEnter={false}
        unmountOnExit={true}
        in={isOpen}
        timeout={{ enter: ANIMATION_ENTER, exit: ANIMATION_EXIT }}
      >
        {state => (
          <TooltipContainer
            ref={tooltipRef}
            transitionState={state}
            style={{
              ...ttStyles,
            }}
          >
            <TooltipInner>
              <TooltipContent withCloseIcon={withCloseIcon}>
                {children}
              </TooltipContent>
              {withCloseIcon ? (
                <ButtonCloseWrapper>
                  <ButtonClose size={'small'} handleClick={onCrossClick} />
                </ButtonCloseWrapper>
              ) : null}
              <TooltipArrow
                ref={arrowRef}
                position={arrowPositionPostfix}
                style={arrowStyles}
              />
            </TooltipInner>
          </TooltipContainer>
        )}
      </Transition>
    )
  })
  return ReactDOM.createPortal(renderTooltip(), domNode.current)
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  originRef: PropTypes.object.isRequired,
  position: PropTypes.oneOf([TOP, BOTTOM, RIGHT, LEFT]).isRequired,
  deltaArrow: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  topIndentNode: PropTypes.object,
  leftIndentNode: PropTypes.object,
  withCloseIcon: PropTypes.bool,
  onCrossClick: PropTypes.func,
  indents: PropTypes.object,
}

Tooltip.defaultProps = {
  indents: {
    ...TOOLTIP_INDENTS,
  },
  deltaArrow: {
    ...DELTA_ARROW,
  },
}

export default Tooltip
